import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { formatPaymentType } from 'components/travel/TravelDetailSection';
import FieldLabel from '../components/FieldLabel';
import ErrorText from '../components/ErrorText';
import { getFieldLabel } from '../utils/formHelpers';

const VISIBLE_TEMPLATE_COUNT = 5;
const TEMPLATE_ROW_HEIGHT = 72;
const TEMPLATE_ROW_GAP = 16;
const TEMPLATE_LIST_PADDING = 32;
const PAYMENT_TEMPLATE_LIST_MAX_HEIGHT =
    TEMPLATE_LIST_PADDING
    + VISIBLE_TEMPLATE_COUNT * TEMPLATE_ROW_HEIGHT
    + (VISIBLE_TEMPLATE_COUNT - 1) * TEMPLATE_ROW_GAP;

const TemplateSelectField = ({
    field,
    data,
    hasError,
    paymentTemplates = [],
    onToggleTemplate,
    onNewTemplate,
    onEditTemplate,
}) => (
    <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
                <MDTypography variant="h6" fontWeight="medium">
                    <FieldLabel label={getFieldLabel(field)} tooltip={field.tooltip} />
                </MDTypography>
                <MDTypography variant="caption" color="text">
                    {field.description}
                </MDTypography>
            </Box>
            <MDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={onNewTemplate}
            >
                New Template
            </MDButton>
        </Box>

        <Stack
            spacing={2}
            sx={{
                border: '1px solid',
                borderColor: hasError ? 'error.main' : 'divider',
                borderRadius: 2,
                p: 2,
                maxHeight: PAYMENT_TEMPLATE_LIST_MAX_HEIGHT,
                overflowY: 'auto',
            }}
        >
            {paymentTemplates.length === 0 ? (
                <MDTypography variant="caption" color="text" sx={{ p: 2, display: 'block' }}>
                    No payment templates yet. Create one to get started.
                </MDTypography>
            ) : paymentTemplates.map((template) => {
                const isSelected = (data[field.key] || []).includes(template.id);
                return (
                    <Box
                        key={template.id}
                        sx={{
                            border: '1px dashed',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            p: 1.5,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => onToggleTemplate(field.key, template.id)}
                                sx={{ alignSelf: 'flex-start', p: 0.5 }}
                            />
                            <Box>
                                <MDTypography fontWeight="medium" variant="body2">
                                    {template.template_name}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                    Payment Type: {formatPaymentType(template.payment_type)}
                                </MDTypography>
                                {template.deposit_percentage != null && (
                                    <MDTypography variant="caption" color="text">
                                        Deposit: {template.deposit_percentage}%
                                    </MDTypography>
                                )}
                                {template.number_of_installments != null && (
                                    <MDTypography variant="caption" color="text" sx={{ ml: '25px' }}>
                                        Installments: {template.number_of_installments}
                                    </MDTypography>
                                )}
                            </Box>
                        </Box>
                        <IconButton size="small" onClick={() => onEditTemplate(template)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            })}
        </Stack>
        {hasError && <ErrorText message="Please select at least one payment template" />}
    </Box>
);

export default TemplateSelectField;
